import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAtom } from "jotai";
import { Box } from "@mui/material";
import SidebarComponent from "../ConstructionCheckList/Sidebar";
import setupAtoms, {
  fetchChecklistData,
  fetchPierLegends,
  fetchtrackertypeData,
  fetchTypeData,
  getcpChecklist,
} from "../../../atoms/setup/SetupAtoms";
import settingsAtom from "../../../atoms/settings/SettingsAtom";
import TabComponent from "./ParentComponent";
import { getView } from "../../../shared/Helper";
import axiosInstance from "../../../utils/axios";

const ConstructionProgress = () => {
  const [data] = useAtom(setupAtoms);
  const [, setcpChecklist] = useAtom(getcpChecklist);
  const [, setChecklist] = useAtom(fetchChecklistData);
  const [, setPierLeget] = useAtom(fetchPierLegends);
  const [, setType] = useAtom(fetchTypeData);
  const [, setSvgdata] = useAtom(fetchtrackertypeData);

  const [isEdit, setIsEdit] = useState(false);
  const [loader, setLoader] = useState(false);
  const [tabLoader, setTabLoader] = useState(false);

  const [active, setActive] = useState("checklist");
  const [view, setViewState] = useState("Combined"); 
  const [checklistView, setChecklistView] = useState("Template Checklist");
  const [value, setValue] = useState(0);
  const [selectedTt, setSelectedTt] = useState([]);
  const [selectedPier, setSelectedPier] = useState([]);

  const [customFields, setCustomFields] = useState({ piers: [], structures: [], module: [] });
  const [checkpoints, setCheckpoints] = useState({ piers: [], structures: [], module: [] });
  const [listData, setListData] = useState({ piers: {}, structures: {}, module: {} });

  const [settingData] = useAtom(settingsAtom);
  const currentProject = useMemo(() => {
    return settingData.projectList.find(
      (o) => o.id === parseInt(localStorage.getItem("project_id"))
    );
  }, [settingData.projectList]);

  const { cpChecklist, checklistData, typeData } = data;
  const lastFetchedKey = useRef(null);
  const [checklistKey, setChecklistKey] = useState(null);
  const prevFetchKey = useRef(null)


  const skipAutoViewRef = useRef(false); 

  const setView = (newView) => {
    skipAutoViewRef.current = true;
    setViewState(newView);
  };

  const fetchChecklist = useCallback(
    async (checklistId) => {

      if (!checklistId || prevFetchKey?.current === checklistId) return;

      prevFetchKey.current = checklistId

      setTabLoader(true);
      try {
        const res = await axiosInstance.get(
          `/projects/fetch_checklist?checklist_id=${checklistId}`
        );
        if (res?.data) {
          const viewKey = getView(value);
          setListData({
            piers: viewKey === "piers" ? res.data || {} : {},
            structures: viewKey === "structures" ? res.data || {} : {},
            module: viewKey === "module" ? res.data || {} : {},
          });
          setCheckpoints({
            piers: viewKey === "piers" ? res.data?.checkpoints || [] : [],
            structures: viewKey === "structures" ? res.data?.checkpoints || [] : [],
            module: viewKey === "module" ? res.data?.checkpoints || [] : [],
          });
          setCustomFields({
            piers: viewKey === "piers" ? res.data?.custom_inputs || [] : [],
            structures: viewKey === "structures" ? res.data?.custom_inputs || [] : [],
            module: viewKey === "module" ? res.data?.custom_inputs || [] : [],
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setTabLoader(false);
      }
    },
    [value]
  );

  useEffect(() => {
    const fetchInitial = async () => {
      setLoader(true);
      try {
        await setcpChecklist("");
        await setChecklist("");
        await setType("");
      } catch (error) {
        console.error(error);
      } finally {
        setLoader(false);
      }
    };
    fetchInitial();
  }, [setcpChecklist, setChecklist, setType]);

  const fetchlist = async (id) => {
    setTabLoader(true)
    try {
      await Promise.all([
      setType(""),
      setPierLeget(""),
      setSvgdata(id)
      ])
    } catch(err) {
      console.error(err)
    } finally {
      setTabLoader(false)
    }
  }

  useEffect(() => {
    const temp = cpChecklist.find((item) => item?.tracker_type === active);
    if (active !== "checklist" && temp?.id) {
     fetchlist(temp?.id)
    }
    // eslint-disable-next-line
  }, [active, cpChecklist, setType, setPierLeget, setSvgdata]);

  useEffect(() => {
    const viewKey = getView(value);

    if (active === "checklist") {
      const key =
        checklistView === "Template Checklist"
          ? `${viewKey === "structures" ? "structure" : viewKey}_0`
          : checklistView === "ITR Checklist"
          ? `${viewKey === "structures" ? "structure" : viewKey}_1`
          : null;
      setChecklistKey(`${key}_${active}`);
      return;
    }

    if (view === "Combined") {
      const temp = cpChecklist.find((item) => item?.tracker_type === active);
      if (temp?.id) {
        const key = `${temp.id}_${viewKey === "structures" ? "structure" : viewKey}_${active}`;
        setChecklistKey(key);
      }
      return;
    }

    setChecklistKey(null);
  }, [view, value, active, cpChecklist, checklistView]);

  useEffect(() => {
    if (view !== "Individual") return;
    const viewKey = getView(value);
    if (viewKey === "piers" && selectedPier?.[0]?.pier_type_id) {
      setChecklistKey(`${selectedPier[0].pier_type_id}_${viewKey}_${active}`);
    }
    if (viewKey === "structures" && selectedTt?.[0]?.tt_type_id) {
      setChecklistKey(`${selectedTt[0].tt_type_id}_structure_${active}`);
    }
    if (viewKey === "module" && selectedTt?.[0]?.tt_type_id) {
      setChecklistKey(`${selectedTt[0].tt_type_id}_${viewKey}_${active}`);
    }
  }, [view, value, selectedPier, selectedTt, active]);

  useEffect(() => {
    if (skipAutoViewRef.current) {
      skipAutoViewRef.current = false; 
      return;
    }

    const viewKey = getView(value);
    const temp = cpChecklist?.find((item) => item?.tracker_type === active);
    const key = `${temp?.id}_${viewKey === "structures" ? "structure" : viewKey}`;
    if (Object.keys(typeData)?.length && typeData?.[key] === 0) {
      setViewState("Combined");
    } else {
      setViewState("Individual");
    }
  }, [cpChecklist, typeData, value, active]);

  useEffect(() => {
    if (!checklistKey || lastFetchedKey.current === checklistKey || !Object.keys(checklistData)?.length) return;
    const result = checklistKey?.split('_').slice(0, 2).join('_');

    const checklistId = checklistData?.[result];

    if (checklistId) {
      lastFetchedKey.current = checklistKey;      
      fetchChecklist(checklistId);
    }
     // eslint-disable-next-line
  }, [checklistKey, checklistData, fetchChecklist, active]);


  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <Box sx={{ height: "calc(100vh - 116px)", width: "23%", borderRight: "1px solid #e0e0e0" }}>
        <SidebarComponent
          data={cpChecklist}
          loader={loader}
          title="QC Checklist"
          setActive={setActive}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          active={active}
          setView={setView}
          viewKey={getView(value)}
          typeData={typeData}
        />
      </Box>
      <Box sx={{ height: "calc(100vh - 116px)", width: "77%" }}>
        <TabComponent
          currentProject={currentProject}
          setView={setView}
          view={view}
          active={active}
          setActive={setActive}
          customFields={customFields}
          setCustomFields={setCustomFields}
          checkpoints={checkpoints}
          setCheckpoints={setCheckpoints}
          value={value}
          setValue={setValue}
          listData={listData}
          loading={loader}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          data={data}
          tabLoader={tabLoader}
          setSelectedTt={setSelectedTt}
          setSelectedPier={setSelectedPier}
          selectedPier={selectedPier}
          selectedTt={selectedTt}
          checklistView={checklistView}
          setChecklistView={setChecklistView}
        />
      </Box>
    </Box>
  );
};

export default ConstructionProgress;
